import React, { useEffect, useMemo, useState } from 'react';
import { PageLayout } from '../../components/layout';
import {
  CmsBlogPost,
  CmsContentBlock,
  createCmsBlogPost,
  deleteCmsBlogPost,
  deleteCmsContentBlock,
  getCmsContent,
  listCmsBlogPosts,
  publishCmsBlogPost,
  saveCmsContentBlocks,
  triggerCmsDeploy,
  updateCmsBlogPost,
} from '../../services/CmsService';
import { showErrorToast, showSuccessToast } from '../../lib/ToastUtility';
import { WEBSITE_CMS_EXTERNAL_URL } from '../../lib/constants/cmsAccess';

type CmsTab = 'content' | 'blog';

type BlogFormState = {
  id?: number;
  title: string;
  slug: string;
  excerpt: string;
  body: string;
  coverImageUrl: string;
  authorName: string;
  category: string;
  tags: string;
  published: boolean;
};

type LanguageCode = 'en' | 'ar' | 'fil';
type LocalizedValue = Record<LanguageCode, string>;
type LocalizedBlogFields = {
  title: LocalizedValue;
  category: LocalizedValue;
  excerpt: LocalizedValue;
  body: LocalizedValue;
};

const defaultContentBlock: CmsContentBlock = {
  pageKey: 'home',
  sectionKey: 'hero',
  fieldKey: 'title',
  value: '',
  valueType: 'TEXT',
};

const defaultBlogForm: BlogFormState = {
  title: '',
  slug: '',
  excerpt: '',
  body: '',
  coverImageUrl: '',
  authorName: '',
  category: '',
  tags: '',
  published: false,
};

const languageLabels: Record<LanguageCode, string> = {
  en: 'English (EN)',
  ar: 'Arabic (AR)',
  fil: 'Filipino (FIL)',
};

const emptyLocalizedValue: LocalizedValue = {
  en: '',
  ar: '',
  fil: '',
};

const emptyLocalizedBlogFields: LocalizedBlogFields = {
  title: { ...emptyLocalizedValue },
  category: { ...emptyLocalizedValue },
  excerpt: { ...emptyLocalizedValue },
  body: { ...emptyLocalizedValue },
};

const isLocalizedObject = (value: unknown): value is LocalizedValue => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  const input = value as Record<string, unknown>;
  return (
    (typeof input.en === 'string' || input.en === undefined) &&
    (typeof input.ar === 'string' || input.ar === undefined) &&
    (typeof input.fil === 'string' || input.fil === undefined)
  );
};

const parseLocalizedValue = (rawValue: string): LocalizedValue => {
  if (!rawValue) {
    return emptyLocalizedValue;
  }

  try {
    const parsed = JSON.parse(rawValue);
    if (isLocalizedObject(parsed)) {
      return {
        en: parsed.en || '',
        ar: parsed.ar || '',
        fil: parsed.fil || '',
      };
    }
  } catch {
    return {
      en: rawValue,
      ar: '',
      fil: '',
    };
  }

  return {
    en: rawValue,
    ar: '',
    fil: '',
  };
};

const stringifyLocalizedValue = (value: LocalizedValue): string => {
  return JSON.stringify(value);
};

type CmsPresetEntry = {
  pageKey: string;
  sectionKey: string;
  fieldKey: string;
};

const WEBSITE_CMS_PRESET_KEYS: CmsPresetEntry[] = [
  // Home
  { pageKey: 'home', sectionKey: 'hero', fieldKey: 'titlePrefix' },
  { pageKey: 'home', sectionKey: 'hero', fieldKey: 'titleHighlightOne' },
  { pageKey: 'home', sectionKey: 'hero', fieldKey: 'titleHighlightTwo' },
  { pageKey: 'home', sectionKey: 'hero', fieldKey: 'subtitle' },
  { pageKey: 'home', sectionKey: 'hero', fieldKey: 'ctaLabel' },
  { pageKey: 'home', sectionKey: 'hero', fieldKey: 'imageUrl' },
  { pageKey: 'home', sectionKey: 'hero', fieldKey: 'imageAlt' },
  { pageKey: 'home', sectionKey: 'hero', fieldKey: 'statsJson' },
  { pageKey: 'home', sectionKey: 'who-we-are', fieldKey: 'title' },
  { pageKey: 'home', sectionKey: 'who-we-are', fieldKey: 'paragraphOne' },
  { pageKey: 'home', sectionKey: 'who-we-are', fieldKey: 'paragraphTwo' },
  { pageKey: 'home', sectionKey: 'problem', fieldKey: 'title' },
  { pageKey: 'home', sectionKey: 'problem', fieldKey: 'subtitle' },
  { pageKey: 'home', sectionKey: 'problem', fieldKey: 'itemsJson' },
  { pageKey: 'home', sectionKey: 'solutions', fieldKey: 'title' },
  { pageKey: 'home', sectionKey: 'solutions', fieldKey: 'subtitle' },
  { pageKey: 'home', sectionKey: 'solutions', fieldKey: 'foodBarsTitle' },
  { pageKey: 'home', sectionKey: 'solutions', fieldKey: 'foodBarsDescription' },
  { pageKey: 'home', sectionKey: 'solutions', fieldKey: 'foodBarsImageUrl' },
  { pageKey: 'home', sectionKey: 'solutions', fieldKey: 'foodBarsImageAlt' },
  { pageKey: 'home', sectionKey: 'solutions', fieldKey: 'trackImpactTitle' },
  { pageKey: 'home', sectionKey: 'solutions', fieldKey: 'trackImpactDescription' },
  { pageKey: 'home', sectionKey: 'solutions', fieldKey: 'trackImpactImageUrl' },
  { pageKey: 'home', sectionKey: 'solutions', fieldKey: 'trackImpactImageAlt' },
  { pageKey: 'home', sectionKey: 'solutions', fieldKey: 'bulletItemsJson' },
  { pageKey: 'home', sectionKey: 'how-it-works', fieldKey: 'title' },
  { pageKey: 'home', sectionKey: 'how-it-works', fieldKey: 'subtitle' },
  { pageKey: 'home', sectionKey: 'how-it-works', fieldKey: 'ctaLabel' },
  { pageKey: 'home', sectionKey: 'how-it-works', fieldKey: 'stepsJson' },
  { pageKey: 'home', sectionKey: 'why-different', fieldKey: 'title' },
  { pageKey: 'home', sectionKey: 'why-different', fieldKey: 'subtitle' },
  { pageKey: 'home', sectionKey: 'why-different', fieldKey: 'itemsJson' },
  { pageKey: 'home', sectionKey: 'impact-metrics', fieldKey: 'title' },
  { pageKey: 'home', sectionKey: 'impact-metrics', fieldKey: 'subtitle' },
  { pageKey: 'home', sectionKey: 'impact-metrics', fieldKey: 'itemsJson' },
  { pageKey: 'home', sectionKey: 'impact-metrics', fieldKey: 'cardTitle' },
  { pageKey: 'home', sectionKey: 'impact-metrics', fieldKey: 'cardDescription' },
  { pageKey: 'home', sectionKey: 'impact-metrics', fieldKey: 'cardCtaLabel' },
  { pageKey: 'home', sectionKey: 'impact-metrics', fieldKey: 'cardImageUrl' },
  { pageKey: 'home', sectionKey: 'impact-metrics', fieldKey: 'cardImageAlt' },
  { pageKey: 'home', sectionKey: 'testimonials', fieldKey: 'title' },
  { pageKey: 'home', sectionKey: 'testimonials', fieldKey: 'subtitle' },
  { pageKey: 'home', sectionKey: 'testimonials', fieldKey: 'ctaLabel' },
  { pageKey: 'home', sectionKey: 'testimonials', fieldKey: 'itemsJson' },
  // Nutrition
  { pageKey: 'nutrition', sectionKey: 'hero', fieldKey: 'title' },
  { pageKey: 'nutrition', sectionKey: 'hero', fieldKey: 'subtitle' },
  { pageKey: 'nutrition', sectionKey: 'hero', fieldKey: 'statTitle' },
  { pageKey: 'nutrition', sectionKey: 'hero', fieldKey: 'statText' },
  { pageKey: 'nutrition', sectionKey: 'hero', fieldKey: 'ctaLabel' },
  { pageKey: 'nutrition', sectionKey: 'hero', fieldKey: 'imageUrl' },
  { pageKey: 'nutrition', sectionKey: 'hero', fieldKey: 'imageAlt' },
  { pageKey: 'nutrition', sectionKey: 'benefits', fieldKey: 'title' },
  { pageKey: 'nutrition', sectionKey: 'benefits', fieldKey: 'itemsJson' },
  { pageKey: 'nutrition', sectionKey: 'ingredients', fieldKey: 'title' },
  { pageKey: 'nutrition', sectionKey: 'ingredients', fieldKey: 'subtitle' },
  { pageKey: 'nutrition', sectionKey: 'ingredients', fieldKey: 'itemsJson' },
  { pageKey: 'nutrition', sectionKey: 'spirulina', fieldKey: 'title' },
  { pageKey: 'nutrition', sectionKey: 'spirulina', fieldKey: 'description' },
  { pageKey: 'nutrition', sectionKey: 'support-options', fieldKey: 'title' },
  { pageKey: 'nutrition', sectionKey: 'support-options', fieldKey: 'itemsJson' },
  { pageKey: 'nutrition', sectionKey: 'use-cases', fieldKey: 'title' },
  { pageKey: 'nutrition', sectionKey: 'use-cases', fieldKey: 'itemsJson' },
  { pageKey: 'nutrition', sectionKey: 'zimbabwe-farm', fieldKey: 'title' },
  { pageKey: 'nutrition', sectionKey: 'zimbabwe-farm', fieldKey: 'description' },
  // Take Action
  { pageKey: 'take-action', sectionKey: 'hero', fieldKey: 'title' },
  { pageKey: 'take-action', sectionKey: 'hero', fieldKey: 'subtitle' },
  { pageKey: 'take-action', sectionKey: 'hero', fieldKey: 'ctaLabel' },
  { pageKey: 'take-action', sectionKey: 'steps', fieldKey: 'title' },
  { pageKey: 'take-action', sectionKey: 'steps', fieldKey: 'subtitle' },
  { pageKey: 'take-action', sectionKey: 'steps', fieldKey: 'itemsJson' },
  { pageKey: 'take-action', sectionKey: 'features', fieldKey: 'title' },
  { pageKey: 'take-action', sectionKey: 'features', fieldKey: 'subtitle' },
  { pageKey: 'take-action', sectionKey: 'features', fieldKey: 'itemsJson' },
  { pageKey: 'take-action', sectionKey: 'testimonials', fieldKey: 'title' },
  { pageKey: 'take-action', sectionKey: 'testimonials', fieldKey: 'itemsJson' },
  { pageKey: 'take-action', sectionKey: 'cta', fieldKey: 'title' },
  { pageKey: 'take-action', sectionKey: 'cta', fieldKey: 'subtitle' },
  { pageKey: 'take-action', sectionKey: 'cta', fieldKey: 'primaryCtaLabel' },
  { pageKey: 'take-action', sectionKey: 'cta', fieldKey: 'secondaryCtaLabel' },
  // Learn
  { pageKey: 'learn', sectionKey: 'hero', fieldKey: 'title' },
  { pageKey: 'learn', sectionKey: 'hero', fieldKey: 'subtitle' },
  { pageKey: 'learn', sectionKey: 'our-story', fieldKey: 'paragraphOne' },
  { pageKey: 'learn', sectionKey: 'our-story', fieldKey: 'paragraphTwo' },
  { pageKey: 'learn', sectionKey: 'our-story', fieldKey: 'paragraphThree' },
  { pageKey: 'learn', sectionKey: 'our-story', fieldKey: 'paragraphFour' },
  { pageKey: 'learn', sectionKey: 'our-story', fieldKey: 'paragraphFive' },
  { pageKey: 'learn', sectionKey: 'our-story', fieldKey: 'imageUrl' },
  { pageKey: 'learn', sectionKey: 'vision', fieldKey: 'title' },
  { pageKey: 'learn', sectionKey: 'vision', fieldKey: 'description' },
  { pageKey: 'learn', sectionKey: 'values', fieldKey: 'title' },
  { pageKey: 'learn', sectionKey: 'values', fieldKey: 'itemsJson' },
  { pageKey: 'learn', sectionKey: 'journey', fieldKey: 'title' },
  { pageKey: 'learn', sectionKey: 'journey', fieldKey: 'itemsJson' },
  // Contact Us
  { pageKey: 'contact-us', sectionKey: 'hero', fieldKey: 'title' },
  { pageKey: 'contact-us', sectionKey: 'hero', fieldKey: 'subtitle' },
  { pageKey: 'contact-us', sectionKey: 'general', fieldKey: 'sectionTitle' },
  { pageKey: 'contact-us', sectionKey: 'general', fieldKey: 'title' },
  { pageKey: 'contact-us', sectionKey: 'general', fieldKey: 'description' },
  { pageKey: 'contact-us', sectionKey: 'general', fieldKey: 'email' },
  { pageKey: 'contact-us', sectionKey: 'press', fieldKey: 'title' },
  { pageKey: 'contact-us', sectionKey: 'press', fieldKey: 'description' },
  { pageKey: 'contact-us', sectionKey: 'press', fieldKey: 'email' },
  { pageKey: 'contact-us', sectionKey: 'locations', fieldKey: 'sectionTitle' },
  { pageKey: 'contact-us', sectionKey: 'locations', fieldKey: 'uaeTitle' },
  { pageKey: 'contact-us', sectionKey: 'locations', fieldKey: 'uaeAddress' },
  { pageKey: 'contact-us', sectionKey: 'locations', fieldKey: 'ukTitle' },
  { pageKey: 'contact-us', sectionKey: 'locations', fieldKey: 'ukAddress' },
  { pageKey: 'contact-us', sectionKey: 'locations', fieldKey: 'footerText' },
  { pageKey: 'contact-us', sectionKey: 'newsletter', fieldKey: 'title' },
  { pageKey: 'contact-us', sectionKey: 'newsletter', fieldKey: 'description' },
  // Blog listing page
  { pageKey: 'blog', sectionKey: 'hero', fieldKey: 'title' },
  { pageKey: 'blog', sectionKey: 'hero', fieldKey: 'subtitle' },
];

const inferValueType = (fieldKey: string): CmsContentBlock['valueType'] => {
  const normalizedField = fieldKey.toLowerCase();

  if (normalizedField.endsWith('json')) {
    return 'JSON';
  }

  if (normalizedField.includes('imageurl')) {
    return 'IMAGE_URL';
  }

  return 'TEXT';
};

const CmsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<CmsTab>('blog');

  const [loadingContent, setLoadingContent] = useState(false);
  const [savingContent, setSavingContent] = useState(false);
  const [contentBlocks, setContentBlocks] = useState<CmsContentBlock[]>([]);
  const [contentForm, setContentForm] = useState<CmsContentBlock>(defaultContentBlock);
  const [localizedFormValue, setLocalizedFormValue] = useState<LocalizedValue>(emptyLocalizedValue);

  const [loadingBlog, setLoadingBlog] = useState(false);
  const [blogPosts, setBlogPosts] = useState<CmsBlogPost[]>([]);
  const [blogForm, setBlogForm] = useState<BlogFormState>(defaultBlogForm);
  const [blogLocalizedFields, setBlogLocalizedFields] = useState<LocalizedBlogFields>(emptyLocalizedBlogFields);
  const [submittingBlog, setSubmittingBlog] = useState(false);
  const [isDeployNoticeOpen, setIsDeployNoticeOpen] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployNoticeContent, setDeployNoticeContent] = useState({
    title: 'Automated Deployment Dispatched',
    subtitle: 'Changes saved to CMS database',
  });

  const handleManualDeploy = async () => {
    setIsDeploying(true);
    try {
      await triggerCmsDeploy('e2g-website', 'Manual re-deploy requested from Admin Operations');
      setDeployNoticeContent({
        title: 'Deployment Job Triggered',
        subtitle: 'Automated build initiated on Google Cloud Build',
      });
      setIsDeployNoticeOpen(true);
      showSuccessToast('Deployment triggered successfully! (~4-5 mins)');
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to trigger deployment');
    } finally {
      setIsDeploying(false);
    }
  };

  const contentKey = (block: Pick<CmsContentBlock, 'pageKey' | 'sectionKey' | 'fieldKey'>) =>
    `${block.pageKey.toLowerCase()}::${block.sectionKey.toLowerCase()}::${block.fieldKey.toLowerCase()}`;

  const loadContent = async () => {
    setLoadingContent(true);
    try {
      const response = await getCmsContent();
      setContentBlocks(response.blocks || []);
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to load CMS content blocks');
    } finally {
      setLoadingContent(false);
    }
  };

  const loadBlogPosts = async () => {
    setLoadingBlog(true);
    try {
      const response = await listCmsBlogPosts({ page: 1, limit: 100 });
      setBlogPosts(response.data || []);
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to load blog posts');
    } finally {
      setLoadingBlog(false);
    }
  };

  useEffect(() => {
    loadContent();
    loadBlogPosts();
  }, []);

  useEffect(() => {
    if (contentForm.valueType === 'JSON') {
      return;
    }

    setLocalizedFormValue(parseLocalizedValue(contentForm.value));
  }, [contentForm.value, contentForm.valueType]);

  const sortedBlocks = useMemo(() => {
    return [...contentBlocks].sort((a, b) => {
      const left = `${a.pageKey}.${a.sectionKey}.${a.fieldKey}`;
      const right = `${b.pageKey}.${b.sectionKey}.${b.fieldKey}`;
      return left.localeCompare(right);
    });
  }, [contentBlocks]);

  const upsertLocalBlock = () => {
    if (!contentForm.pageKey.trim() || !contentForm.sectionKey.trim() || !contentForm.fieldKey.trim()) {
      showErrorToast('pageKey, sectionKey and fieldKey are required');
      return;
    }

    const key = contentKey(contentForm);

    setContentBlocks((previous) => {
      const nextBlock: CmsContentBlock = {
        ...contentForm,
        pageKey: contentForm.pageKey.trim().toLowerCase(),
        sectionKey: contentForm.sectionKey.trim().toLowerCase(),
        fieldKey: contentForm.fieldKey.trim().toLowerCase(),
        value: contentForm.value,
      };

      const index = previous.findIndex((item) => contentKey(item) === key);
      if (index < 0) {
        return [...previous, nextBlock];
      }

      const updated = [...previous];
      updated[index] = { ...updated[index], ...nextBlock };
      return updated;
    });

    setContentForm(defaultContentBlock);
    setLocalizedFormValue(emptyLocalizedValue);
  };

  const updateLocalizedContentValue = (language: LanguageCode, value: string) => {
    setLocalizedFormValue((previous) => {
      const next = {
        ...previous,
        [language]: value,
      };

      setContentForm((current) => ({
        ...current,
        value: stringifyLocalizedValue(next),
      }));

      return next;
    });
  };

  const persistContent = async () => {
    setSavingContent(true);
    try {
      await saveCmsContentBlocks(contentBlocks);
      showSuccessToast('Content blocks saved! Deployment triggered (~4-5 mins)');
      setDeployNoticeContent({
        title: 'Content Blocks Saved & Deployment Initiated',
        subtitle: `${contentBlocks.length} content block(s) saved to database`,
      });
      setIsDeployNoticeOpen(true);
      await loadContent();
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to save content blocks');
    } finally {
      setSavingContent(false);
    }
  };

  const seedWebsitePresets = () => {
    const existing = new Set(contentBlocks.map((item) => contentKey(item)));

    const missingBlocks: CmsContentBlock[] = WEBSITE_CMS_PRESET_KEYS
      .filter((entry) => {
        const key = contentKey(entry);
        return !existing.has(key);
      })
      .map((entry) => ({
        pageKey: entry.pageKey,
        sectionKey: entry.sectionKey,
        fieldKey: entry.fieldKey,
        valueType: inferValueType(entry.fieldKey),
        value: '',
      }));

    if (missingBlocks.length === 0) {
      showSuccessToast('All website CMS preset keys are already present');
      return;
    }

    setContentBlocks((previous) => [...previous, ...missingBlocks]);
    showSuccessToast(`Added ${missingBlocks.length} missing preset keys. Click "Save All Blocks" to persist.`);
  };

  const removeContentBlock = async (block: CmsContentBlock) => {
    try {
      await deleteCmsContentBlock(block);
      setContentBlocks((previous) => previous.filter((item) => contentKey(item) !== contentKey(block)));
      showSuccessToast('Content block deleted');
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to delete content block');
    }
  };

  const startEditBlog = (post: CmsBlogPost) => {
    const titleLocalized = parseLocalizedValue(post.title || '');
    const categoryLocalized = parseLocalizedValue(post.category || '');
    const excerptLocalized = parseLocalizedValue(post.excerpt || '');
    const bodyLocalized = parseLocalizedValue(post.body || '');

    setBlogForm({
      id: post.id,
      title: titleLocalized.en || post.title,
      slug: post.slug,
      excerpt: excerptLocalized.en || post.excerpt,
      body: bodyLocalized.en || post.body,
      coverImageUrl: post.coverImageUrl,
      authorName: post.authorName || '',
      category: categoryLocalized.en || post.category || '',
      tags: Array.isArray(post.tags) ? post.tags.join(', ') : '',
      published: post.published,
    });

    setBlogLocalizedFields({
      title: titleLocalized,
      category: categoryLocalized,
      excerpt: excerptLocalized,
      body: bodyLocalized,
    });
  };

  const updateBlogLocalizedField = (
    field: keyof LocalizedBlogFields,
    language: LanguageCode,
    value: string,
  ) => {
    setBlogLocalizedFields((previous) => ({
      ...previous,
      [field]: {
        ...previous[field],
        [language]: value,
      },
    }));
  };

  const submitBlogPost = async () => {
    const hasTitle = Object.values(blogLocalizedFields.title).some((value) => value.trim().length > 0);
    if (!hasTitle) {
      showErrorToast('Blog title is required in at least one language');
      return;
    }

    setSubmittingBlog(true);
    try {
      const payload = {
        title: stringifyLocalizedValue(blogLocalizedFields.title),
        slug: blogForm.slug.trim() || undefined,
        excerpt: stringifyLocalizedValue(blogLocalizedFields.excerpt),
        body: stringifyLocalizedValue(blogLocalizedFields.body),
        coverImageUrl: blogForm.coverImageUrl,
        authorName: blogForm.authorName || null,
        category: stringifyLocalizedValue(blogLocalizedFields.category),
        tags: blogForm.tags
          .split(',')
          .map((tag) => tag.trim())
          .filter(Boolean),
        published: blogForm.published,
      };

      if (blogForm.id) {
        await updateCmsBlogPost(blogForm.id, payload);
        showSuccessToast('Blog post updated');
      } else {
        await createCmsBlogPost(payload);
        showSuccessToast('Blog post created');
      }

      if (payload.published) {
        setDeployNoticeContent({
          title: blogForm.id ? 'Blog Post Updated & Deployment Triggered' : 'Blog Post Created & Deployment Triggered',
          subtitle: `Published post "${payload.title}" queued for deployment`,
        });
        setIsDeployNoticeOpen(true);
      }

      setBlogForm(defaultBlogForm);
      setBlogLocalizedFields(emptyLocalizedBlogFields);
      await loadBlogPosts();
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to save blog post');
    } finally {
      setSubmittingBlog(false);
    }
  };

  const togglePublish = async (post: CmsBlogPost) => {
    try {
      await publishCmsBlogPost(post.id, !post.published);
      showSuccessToast(post.published ? 'Post unpublished' : 'Post published! Deployment triggered (~4-5 mins)');
      setDeployNoticeContent({
        title: post.published ? 'Blog Post Unpublished & Deployment Triggered' : 'Blog Post Published & Deployment Triggered',
        subtitle: `Blog post "${post.title}" status updated`,
      });
      setIsDeployNoticeOpen(true);
      await loadBlogPosts();
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to update publish status');
    }
  };

  const removeBlogPost = async (postId: number) => {
    try {
      await deleteCmsBlogPost(postId);
      showSuccessToast('Blog post deleted');
      await loadBlogPosts();
    } catch (error) {
      showErrorToast((error as Error).message || 'Failed to delete blog post');
    }
  };

  return (
    <PageLayout
      title="Website CMS"
      subtitle="Use Visual Editor for all website content updates. Keep blog posting here as the dedicated submission flow."
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setActiveTab('blog')}
            className={`rounded-lg px-4 py-2 text-sm font-medium ${
              activeTab === 'blog'
                ? 'bg-primary-100 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
            }`}
          >
            Blog Posts
          </button>
          <button
            onClick={handleManualDeploy}
            disabled={isDeploying}
            className="flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
            title="Trigger an automated Cloud Build rebuild & deployment for the live website"
          >
            {isDeploying ? 'Deploying...' : '🚀 Re-deploy Live Website'}
          </button>
          <a
            href={WEBSITE_CMS_EXTERNAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100"
          >
            Open Live Website Editor
          </a>
        </div>
      }
    >
      {activeTab === 'blog' ? null : (
        <div className="mx-6 mt-6 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          Website content editing has moved directly to the E2G website. Click &quot;Open Live Website Editor&quot; above to launch the editor at{' '}
          <a
            href={WEBSITE_CMS_EXTERNAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline hover:text-amber-950"
          >
            {WEBSITE_CMS_EXTERNAL_URL}
          </a>.
        </div>
      )}
      {activeTab === 'content' ? (
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <input
              className="rounded-lg border border-gray-300 px-3 py-2"
              placeholder="pageKey (home, learn, blog)"
              value={contentForm.pageKey}
              onChange={(event) => setContentForm((prev) => ({ ...prev, pageKey: event.target.value }))}
            />
            <input
              className="rounded-lg border border-gray-300 px-3 py-2"
              placeholder="sectionKey (hero, newsletter)"
              value={contentForm.sectionKey}
              onChange={(event) => setContentForm((prev) => ({ ...prev, sectionKey: event.target.value }))}
            />
            <input
              className="rounded-lg border border-gray-300 px-3 py-2"
              placeholder="fieldKey (title, imageUrl)"
              value={contentForm.fieldKey}
              onChange={(event) => setContentForm((prev) => ({ ...prev, fieldKey: event.target.value }))}
            />
            <select
              className="rounded-lg border border-gray-300 px-3 py-2"
              value={contentForm.valueType}
              onChange={(event) =>
                setContentForm((prev) => ({
                  ...prev,
                  valueType: event.target.value as CmsContentBlock['valueType'],
                }))
              }
            >
              <option value="TEXT">TEXT</option>
              <option value="MARKDOWN">MARKDOWN</option>
              <option value="IMAGE_URL">IMAGE_URL</option>
              <option value="JSON">JSON</option>
            </select>
            <button
              onClick={upsertLocalBlock}
              className="rounded-lg bg-primary-100 text-white px-4 py-2 font-medium"
            >
              Add Or Update
            </button>
          </div>

          {contentForm.valueType === 'JSON' ? (
            <>
              <textarea
                className="w-full min-h-[140px] rounded-lg border border-gray-300 px-3 py-2"
                placeholder="Content value"
                value={contentForm.value}
                onChange={(event) => setContentForm((prev) => ({ ...prev, value: event.target.value }))}
              />
              <p className="text-xs text-gray-500">
                JSON blocks can also be language-aware by using keys: en, ar, fil.
              </p>
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {(Object.keys(languageLabels) as LanguageCode[]).map((languageCode) => (
                <div key={languageCode} className="space-y-2">
                  <label className="text-xs font-semibold text-gray-700">
                    {languageLabels[languageCode]}
                  </label>
                  <textarea
                    className="w-full min-h-[120px] rounded-lg border border-gray-300 px-3 py-2"
                    placeholder={`Value in ${languageLabels[languageCode]}`}
                    value={localizedFormValue[languageCode]}
                    onChange={(event) => updateLocalizedContentValue(languageCode, event.target.value)}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">{sortedBlocks.length} content blocks</p>
            <div className="flex items-center gap-2">
              <button
                disabled={savingContent || loadingContent}
                onClick={seedWebsitePresets}
                className="rounded-lg border border-amber-300 bg-amber-50 text-amber-800 px-4 py-2 font-medium disabled:opacity-60"
              >
                Seed Website Presets
              </button>
              <button
                disabled={savingContent || loadingContent || sortedBlocks.length === 0}
                onClick={persistContent}
                className="rounded-lg bg-emerald-600 text-white px-4 py-2 font-medium disabled:opacity-60"
              >
                {savingContent ? 'Saving...' : 'Save All Blocks'}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto border border-gray-200 rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="p-3">Page</th>
                  <th className="p-3">Section</th>
                  <th className="p-3">Field</th>
                  <th className="p-3">Type</th>
                  <th className="p-3">Value</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedBlocks.map((block) => (
                  <tr key={contentKey(block)} className="border-t border-gray-100">
                    <td className="p-3">{block.pageKey}</td>
                    <td className="p-3">{block.sectionKey}</td>
                    <td className="p-3">{block.fieldKey}</td>
                    <td className="p-3">{block.valueType}</td>
                    <td className="p-3 max-w-[440px] truncate" title={block.value}>
                      {block.value}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <button
                          className="rounded-md border border-gray-300 px-2 py-1"
                          onClick={() => setContentForm(block)}
                        >
                          Edit
                        </button>
                        <button
                          className="rounded-md border border-red-300 text-red-700 px-2 py-1"
                          onClick={() => removeContentBlock(block)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {sortedBlocks.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-gray-500">
                      {loadingContent ? 'Loading content blocks...' : 'No content blocks found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              className="rounded-lg border border-gray-300 px-3 py-2"
              placeholder="Slug (optional)"
              value={blogForm.slug}
              onChange={(event) => setBlogForm((prev) => ({ ...prev, slug: event.target.value }))}
            />
            <input
              className="rounded-lg border border-gray-300 px-3 py-2"
              placeholder="Cover image URL"
              value={blogForm.coverImageUrl}
              onChange={(event) => setBlogForm((prev) => ({ ...prev, coverImageUrl: event.target.value }))}
            />
            <input
              className="rounded-lg border border-gray-300 px-3 py-2"
              placeholder="Author"
              value={blogForm.authorName}
              onChange={(event) => setBlogForm((prev) => ({ ...prev, authorName: event.target.value }))}
            />
            <input
              className="rounded-lg border border-gray-300 px-3 py-2"
              placeholder="Tags (comma separated)"
              value={blogForm.tags}
              onChange={(event) => setBlogForm((prev) => ({ ...prev, tags: event.target.value }))}
            />
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-700">Multilingual Blog Fields</h4>
            {(Object.keys(languageLabels) as LanguageCode[]).map((languageCode) => (
              <div key={`blog-${languageCode}`} className="border border-gray-200 rounded-lg p-4 space-y-3">
                <p className="text-xs font-semibold text-gray-600">{languageLabels[languageCode]}</p>
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder={`Title (${languageLabels[languageCode]})`}
                  value={blogLocalizedFields.title[languageCode]}
                  onChange={(event) => updateBlogLocalizedField('title', languageCode, event.target.value)}
                />
                <input
                  className="w-full rounded-lg border border-gray-300 px-3 py-2"
                  placeholder={`Category (${languageLabels[languageCode]})`}
                  value={blogLocalizedFields.category[languageCode]}
                  onChange={(event) => updateBlogLocalizedField('category', languageCode, event.target.value)}
                />
                <textarea
                  className="w-full min-h-[100px] rounded-lg border border-gray-300 px-3 py-2"
                  placeholder={`Excerpt (${languageLabels[languageCode]})`}
                  value={blogLocalizedFields.excerpt[languageCode]}
                  onChange={(event) => updateBlogLocalizedField('excerpt', languageCode, event.target.value)}
                />
                <textarea
                  className="w-full min-h-[180px] rounded-lg border border-gray-300 px-3 py-2"
                  placeholder={`Body (${languageLabels[languageCode]})`}
                  value={blogLocalizedFields.body[languageCode]}
                  onChange={(event) => updateBlogLocalizedField('body', languageCode, event.target.value)}
                />
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={blogForm.published}
                onChange={(event) => setBlogForm((prev) => ({ ...prev, published: event.target.checked }))}
              />
              Publish immediately
            </label>

            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setBlogForm(defaultBlogForm);
                  setBlogLocalizedFields(emptyLocalizedBlogFields);
                }}
                className="rounded-lg border border-gray-300 px-4 py-2"
              >
                Clear
              </button>
              <button
                disabled={submittingBlog}
                onClick={submitBlogPost}
                className="rounded-lg bg-primary-100 text-white px-4 py-2 font-medium disabled:opacity-60"
              >
                {submittingBlog ? 'Saving...' : blogForm.id ? 'Update Post' : 'Create Post'}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto border border-gray-200 rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left">
                <tr>
                  <th className="p-3">Title</th>
                  <th className="p-3">Slug</th>
                  <th className="p-3">Category</th>
                  <th className="p-3">Published</th>
                  <th className="p-3">Updated</th>
                  <th className="p-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {blogPosts.map((post) => (
                  <tr key={post.id} className="border-t border-gray-100">
                    <td className="p-3 max-w-[300px] truncate" title={post.title}>{post.title}</td>
                    <td className="p-3">{post.slug}</td>
                    <td className="p-3">{post.category || '-'}</td>
                    <td className="p-3">{post.published ? 'Yes' : 'No'}</td>
                    <td className="p-3">{new Date(post.updatedAt).toLocaleString()}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-2">
                        <button
                          className="rounded-md border border-gray-300 px-2 py-1"
                          onClick={() => startEditBlog(post)}
                        >
                          Edit
                        </button>
                        <button
                          className="rounded-md border border-emerald-300 text-emerald-700 px-2 py-1"
                          onClick={() => togglePublish(post)}
                        >
                          {post.published ? 'Unpublish' : 'Publish'}
                        </button>
                        <button
                          className="rounded-md border border-red-300 text-red-700 px-2 py-1"
                          onClick={() => removeBlogPost(post.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {blogPosts.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-gray-500">
                      {loadingBlog ? 'Loading blog posts...' : 'No blog posts yet.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Deployment Information & Timeline Modal */}
      {isDeployNoticeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl border border-gray-100 animate-fadeIn">
            <div className="flex items-center gap-3 mb-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 font-bold text-lg">
                ✓
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{deployNoticeContent.title}</h3>
                <p className="text-xs text-gray-500">{deployNoticeContent.subtitle}</p>
              </div>
            </div>

            <div className="space-y-3 my-4 text-sm text-gray-600">
              <p className="leading-relaxed">
                Deployment to the live website will take approximately <strong>4–5 minutes</strong> to complete.
              </p>
              <div className="text-xs text-gray-500 bg-gray-50 p-3 rounded-xl border border-gray-200 leading-relaxed">
                💡 Please refresh your browser in ~5 minutes to check. If changes do not load immediately, please perform a <strong>hard refresh</strong> (<kbd className="px-1.5 py-0.5 rounded bg-gray-200 font-mono text-[10px]">Cmd + Shift + R</kbd> on Mac or <kbd className="px-1.5 py-0.5 rounded bg-gray-200 font-mono text-[10px]">Ctrl + F5</kbd> on Windows).
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setIsDeployNoticeOpen(false)}
                className="rounded-xl bg-emerald-600 px-5 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-700 transition-colors cursor-pointer"
              >
                OK, Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default CmsPage;
